import React from 'react';

import Tooltip from '../../library/tooltip/tooltip';
import TreadwellIcon from './components/treadwell-icon/treadwell-icon';
import CircleDollarSolidIcon from '../../shared/icons/circle-dollar-solid-icon';
import { BookmarkSolidIcon, StarSolidIcon } from '../../shared/icons/icons';

import { useAemContent } from '../../shared/hooks/hooks';

import { AEM_CONTENT_PAGE_IDS } from '../../shared/constants/cms';
import { PRODUCT_TILE_TYPES } from '../../shared/constants/treadwell';

import styles from './top-three-tile-header.scss';

const MESSAGES = {
  [PRODUCT_TILE_TYPES.RECOMMENDED]:
    'We recommend these tires based on extensive performance and safety testing, and overall value.',
  [PRODUCT_TILE_TYPES.HIGHEST_RATED]:
    'These tires have the most positive reviews from our customers.',
  [PRODUCT_TILE_TYPES.ORIGINAL_EQUIPMENT]:
    'These are the tires your vehicle is equipped with from the factory.',
  [PRODUCT_TILE_TYPES.LOWEST_PRICE]: 'These are the best value tires for you.',
  [PRODUCT_TILE_TYPES.LOWEST_PRICE_WEB]:
    'These are the best value tires for you.'
};

const aemTopThreeIds = {
  bestSeller: 'plp-top-three.best-seller',
  highestRated: 'plp-top-three.highest-rated',
  lowestPriceAvailable: 'plp-top-three.lowest-price-available',
  lowestPriceOnline: 'plp-top-three.lowest-price-online',
  originalEquipment: 'plp-top-three.original-equipment',
  ourRecommendation: 'plp-top-three.our-recommendation',
  topRecommendation: 'plp-top-three.top-recommendation'
};

interface TopThreeTileHeaderProps {
  isTreadwell?: boolean;
  rank?: number;
  type?: (typeof PRODUCT_TILE_TYPES)[keyof typeof PRODUCT_TILE_TYPES] | null;
}

function TopThreeTileHeader(props: TopThreeTileHeaderProps) {
  const { isTreadwell, rank, type } = props;
  const { aemTooltipsContent } = useAemContent(AEM_CONTENT_PAGE_IDS.global);
  const infoMessage = MESSAGES[type || ''] || '';

  let containerClassName;
  let text;
  let tipText;
  let Icon;

  if (isTreadwell && rank) {
    if (rank > 1) {
      containerClassName = 'our-treadwell-recommendation';
    } else if (rank === 1) {
      containerClassName = 'our-treadwell-recommendation-top';
    }
    text =
      aemTooltipsContent?.[aemTopThreeIds.topRecommendation]?.title ||
      'Top Recommendation';
    tipText =
      aemTooltipsContent?.[aemTopThreeIds.topRecommendation]?.tipText ||
      infoMessage;
  } else if (!rank && type === PRODUCT_TILE_TYPES.RECOMMENDED) {
    containerClassName = 'our-recommendation';
    text =
      aemTooltipsContent?.[aemTopThreeIds.ourRecommendation]?.title ||
      'Our Recommendation';
    tipText =
      aemTooltipsContent?.[aemTopThreeIds.ourRecommendation]?.tipText ||
      infoMessage;
    Icon = BookmarkSolidIcon;
  } else if (type === PRODUCT_TILE_TYPES.ORIGINAL_EQUIPMENT) {
    containerClassName = 'original-equipment';
    text =
      aemTooltipsContent?.[aemTopThreeIds.originalEquipment]?.title ||
      'Original Equipment';
    tipText =
      aemTooltipsContent?.[aemTopThreeIds.originalEquipment]?.tipText ||
      infoMessage;
  } else if (type === PRODUCT_TILE_TYPES.HIGHEST_RATED) {
    containerClassName = 'highest-rated';
    text =
      aemTooltipsContent?.[aemTopThreeIds.highestRated]?.title ||
      'Highest Rated';
    tipText =
      aemTooltipsContent?.[aemTopThreeIds.highestRated]?.tipText || infoMessage;
    Icon = StarSolidIcon;
  } else if (type === PRODUCT_TILE_TYPES.LOWEST_PRICE) {
    containerClassName = 'lowest-price';
    text =
      aemTooltipsContent?.[aemTopThreeIds.lowestPriceAvailable]?.title ||
      'Lowest Price Available';
    tipText =
      aemTooltipsContent?.[aemTopThreeIds.lowestPriceAvailable]?.tipText ||
      infoMessage;
    Icon = CircleDollarSolidIcon;
  } else if (type === PRODUCT_TILE_TYPES.LOWEST_PRICE_WEB) {
    containerClassName = 'lowest-price';
    text =
      aemTooltipsContent?.[aemTopThreeIds.lowestPriceOnline]?.title ||
      'Lowest Price Online';
    tipText =
      aemTooltipsContent?.[aemTopThreeIds.lowestPriceOnline]?.tipText ||
      infoMessage;
    Icon = CircleDollarSolidIcon;
  }

  return (
    <div className={styles[containerClassName]}>
      <div className={styles['header']}>
        <span className={styles['header-icon']}>
          {Boolean(Icon) && <Icon />}
          {isTreadwell && <TreadwellIcon rank={rank} />}
          {type === PRODUCT_TILE_TYPES.ORIGINAL_EQUIPMENT && 'O.E.'}
        </span>
        <span className={styles['header-text']}>{text}</span>
      </div>
      <Tooltip ariaLabel="info" content={tipText} />
    </div>
  );
}

export default TopThreeTileHeader;
